import React, { useState, useEffect } from "react";

import CentralizedModalContainer from "../../../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Icon,
  Input,
  Modal,
  Select,
  Tabs,
  Text,
  TextArea
} from "@theorgchartdev/storybook";
import { FieldListConfiguration } from "./FieldListConfiguration";
import { FieldPicklistConfiguration } from "./FieldPicklistConfiguration";
import { appstr } from "@coreapp/utils/appstr";
import { kDecimalOptions, kFieldOptions } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { getFieldTypeString } from "@coreapp/utils/getFieldType";

const GetNumDecPlaces = fieldFormat => {
  var numDecimals = "0";
  if (fieldFormat["numDecimals"] && kDecimalOptions.indexOf(fieldFormat["numDecimals"]) > -1)
    numDecimals = fieldFormat["numDecimals"];
  return numDecimals;
};

const GetDefaultFormatByType = (header, type) => {
  let defaultFormat = {};

  let fieldType = getFieldTypeString(header, type);
  defaultFormat["type"] = fieldType;

  switch (fieldType) {
    case "Text":
      defaultFormat["type"] = "Text";
      defaultFormat["breakChar"] = "";
      break;
    case "Number":
      defaultFormat["prefix"] = "";
      defaultFormat["postfix"] = "";
      defaultFormat["percentage"] = "";
      defaultFormat["thousandsep"] = true;
      defaultFormat["numDecimals"] = "0";
      break;
    case "Image":
      break;
    case "Date":
      defaultFormat["convertlocal"] = false;
      break;
  }
  return defaultFormat;
};

const BuildTypeOptions = (updateType, dataTestIdPrefix) => {
  let types = [];
  for (let fieldType of kFieldOptions) {
    types.push({
      label: appstr(fieldType),
      dataTestId: dataTestIdPrefix + fieldType,
      value: fieldType,
      clickHandler: () => updateType("type", fieldType)
    });
  }
  return types;
};

const BuildDecimalOptions = updateType => {
  let types = [];
  for (let dec of kDecimalOptions) {
    types.push({
      label: dec,
      value: dec,
      dataTestId: `format-field-decimal-places-${dec}`,
      clickHandler: () => updateType("numDecimals", dec)
    });
  }
  return types;
};

export const FormatFieldModal = props => {
  if (!props.chartConfig) return;

  return <FormatFieldModalContent {...props} />;
};

const FormatFieldModalContent = ({ fieldData, chartConfig, show, onClose, dataTestIdPrefix }) => {
  const headerValue = fieldData.value;
  // chartConfig["localization"].profile stores per-language custom display labels
  const curLangCode = Globals._stringMgr.fCurLangStr;
  let curLabel = null;
  if (
    chartConfig["localization"] &&
    chartConfig["localization"]["profile"] &&
    chartConfig["localization"]["profile"][headerValue] &&
    chartConfig["localization"]["profile"][headerValue][curLangCode]
  )
    curLabel = chartConfig["localization"]["profile"][headerValue][curLangCode];

  const [fieldFormat, setFieldFormat] = useState(null);
  const [label, setLabel] = useState(curLabel);
  const [clonedFieldFormat, setClonedFieldFormat] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [changedFields, setChangedFields] = useState([]);
  const [header, setHeader] = useState(headerValue);
  const [subTypeTab, setSubTypeTab] = useState(0);

  useEffect(() => {
    InitializeFieldFormat();
  }, [chartConfig]);

  const InitializeFieldFormat = () => {
    if (!chartConfig["fieldFormats"]) chartConfig["fieldFormats"] = {};
    var fieldInfo = chartConfig["fieldFormats"][header];
    if (!fieldInfo) {
      fieldInfo = GetDefaultFormatByType(header);
    }
    fieldInfo.thousandsep =
      fieldInfo.thousandsep || fieldInfo.thousandsep == null || fieldInfo.thousandsep === "";
    const fieldInfoObj = JSON.parse(JSON.stringify(fieldInfo));
    if (fieldInfoObj?.listOptions?.enabled) {
      setSubTypeTab(1);
    }

    setFieldFormat(fieldInfoObj);
    setClonedFieldFormat(fieldInfoObj);
  };

  const onSaveFieldFormat = () => {
    let curLabel = null;
    if (label) curLabel = label.trim();

    let saveData = {};
    let updateKeys = ["fieldFormats"];

    const curLangCode = Globals._stringMgr.fCurLangStr;
    const hasExistingLabel = !!chartConfig["localization"]?.profile?.[header]?.[curLangCode];
    if (label !== header || hasExistingLabel) {
      let localization = chartConfig["localization"];
      if (!localization) localization = { profile: {}, views: {} };
      else if (!localization["profile"]) localization["profile"] = {};
      else if (!localization["profile"][header]) localization["profile"][header] = {};

      if (!localization["profile"][header]) localization["profile"][header] = {};

      if (curLabel && curLabel !== header) localization["profile"][header][curLangCode] = curLabel;
      else delete localization["profile"][header][curLangCode];

      saveData["localization"] = JSON.parse(JSON.stringify(localization));
      updateKeys.push("localization");
    }

    let existingFieldFormats = chartConfig["fieldFormats"]
      ? JSON.parse(JSON.stringify(chartConfig["fieldFormats"]))
      : {};
    existingFieldFormats[header] = JSON.parse(JSON.stringify(fieldFormat));
    saveData["fieldFormats"] = existingFieldFormats;

    onClose(saveData, updateKeys);
  };

  const handleChange = (key, value) => {
    setFieldFormat(prevFieldFormat => ({
      ...prevFieldFormat,
      [key]: value
    }));
    ChangedField("fieldFormats");
  };

  const handleSubTypeEnable = subType => () => {
    let payload = {
      listOptions: {},
      pickListOptions: {}
    };

    switch (subType) {
      case "picklist":
        payload = { listOptions: { enabled: false } };
        break;
      case "list":
        payload = { pickListOptions: { isPicklist: false } };
        break;
    }

    setFieldFormat(format => ({
      ...format,
      listOptions: {
        ...format.listOptions,
        ...payload.listOptions
      },
      pickListOptions: {
        ...format.pickListOptions,
        ...payload.pickListOptions
      }
    }));
  };

  const ChangedField = key => {
    if (!changedFields.includes(key)) setChangedFields([...changedFields, key]);
  };

  const handleLabelChange = e => {
    const value = e.target.value;
    setLabel(value);
  };

  const onCancel = () => {
    if (isDirty) return;
    onClose(false);
  };

  const contentClass = "oc-modal-content";

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="format-field-modal"
        show={show}
        minWidth={"medium"}
        maxWidth={"medium"}
        fillWidth={true}
        centerScreen={true}
      >
        <DialogTitle
          divider={true}
          onClose={() => onCancel()}
          data-testid={`${dataTestIdPrefix}-format-field-modal-title`}
        >
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"pen-to-square"} />
            {fieldData?.name}
          </span>
        </DialogTitle>
        <DialogContent>
          <Flex direction={"column"} gap={"12px"} className={contentClass}>
            <Input
              dataTestId={`${dataTestIdPrefix}-format-field-label`}
              label={appstr("Label")}
              name={"label"}
              placeholder={fieldData?.name}
              size={"medium"}
              value={label}
              onChange={handleLabelChange}
            />
            <Flex direction={"column"} gap={"8px"}>
              <Text size={"small"} modifier={"bold"}>
                {appstr("Field") + " " + appstr("Type")}
              </Text>
              <Select
                dataTestId="format-field-select-field-type"
                selectSize={"medium"}
                value={fieldFormat?.type}
                options={BuildTypeOptions(handleChange, "format-field-select-field-type-")}
              />
            </Flex>
            <Flex margin={"12px 0 12px 0"}>
              <Divider />
            </Flex>
            <FieldOptionContent
              fieldFormat={fieldFormat}
              onChange={handleChange}
              dataTestIdPrefix={dataTestIdPrefix}
            />
            <Tabs
              tabIndex={subTypeTab}
              onTabChange={setSubTypeTab}
              tabs={[
                {
                  header: appstr("Picklist"),
                  dataTestId: "format-field-modal-tab-picklist",
                  content: (
                    <FieldPicklistConfiguration
                      fieldName={fieldData?.name}
                      fieldFormat={fieldFormat}
                      onEnable={handleSubTypeEnable("picklist")}
                      onChange={handleChange}
                      dataTestIdPrefix={dataTestIdPrefix}
                    />
                  )
                },
                {
                  header: appstr("List"),
                  dataTestId: "format-field-modal-tab-list",
                  content: (
                    <FieldListConfiguration
                      fieldName={fieldData?.name}
                      fieldFormat={fieldFormat}
                      onEnable={handleSubTypeEnable("list")}
                      onChange={handleChange}
                      dataTestIdPrefix={dataTestIdPrefix}
                    />
                  )
                }
              ]}
            />
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary
            dataTestId="format-field-modal-action-cancel"
            onClick={() => onCancel()}
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            dataTestId="format-field-modal-action-save"
            onClick={() => onSaveFieldFormat()}
          >
            {appstr("Save")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

const FieldOptionContent = ({ fieldFormat, onChange, dataTestIdPrefix }) => {
  let content = null;
  if (!fieldFormat) {
    return content;
  }
  switch (fieldFormat.type) {
    case "Text":
      content = (
        <TextFormatOptions
          fieldFormat={fieldFormat}
          onChange={onChange}
          dataTestIdPrefix={dataTestIdPrefix}
        />
      );
      break;
    case "Number":
      content = (
        <NumberFormatOptions
          fieldFormat={fieldFormat}
          onChange={onChange}
          dataTestIdPrefix={dataTestIdPrefix}
        />
      );
      break;
    case "Date":
      content = (
        <DateFormatOptions
          fieldFormat={fieldFormat}
          onChange={onChange}
          dataTestIdPrefix={dataTestIdPrefix}
        />
      );
      break;
    default:
      return;
  }

  return (
    <>
      {content}
      <Flex margin={"12px 0 12px 0"}>
        <Divider />
      </Flex>
    </>
  );
};

export const TextFormatOptions = ({ fieldFormat, onChange, dataTestIdPrefix }) => {
  return (
    <Flex direction={"column"} gap={"8px"}>
      <Text size={"small"} modifier={"bold"}>
        {appstr("breakcharacter")}
      </Text>
      <TextArea
        dataTestId={`${dataTestIdPrefix}-format-field-break-char`}
        value={fieldFormat?.breakChar}
        placeholder={appstr("Enter break character")}
        onChange={e => onChange("breakChar", e.target.value)}
      />
    </Flex>
  );
};

export const NumberFormatOptions = ({ fieldFormat, onChange, dataTestIdPrefix }) => {
  return (
    <Flex direction={"column"} gap={"12px"}>
      <Flex direction={"column"} gap={"8px"}>
        <Text size={"small"} modifier={"bold"}>
          {appstr("DecimalPlaces")}
        </Text>
        <Select
          dataTestId="format-field-decimal-places"
          selectSize={"medium"}
          value={fieldFormat ? GetNumDecPlaces(fieldFormat) : "0"}
          options={BuildDecimalOptions(onChange)}
        />
      </Flex>
      <Flex direction={"column"} gap={"8px"}>
        <Text size={"small"} modifier={"bold"}>
          {appstr("prefix")}
        </Text>
        <TextArea
          dataTestId={"format-field-prefix"}
          value={fieldFormat?.prefix}
          placeholder={appstr("Enter prefix") + ". Ex., $"}
          onChange={e => onChange("prefix", e.target.value)}
        />
      </Flex>
      <Flex direction={"column"} gap={"8px"}>
        <Text size={"small"} modifier={"bold"}>
          {appstr("postfix")}
        </Text>
        <TextArea
          dataTestId={"format-field-postfix"}
          value={fieldFormat?.postfix}
          placeholder={appstr("Enter postfix") + ". Ex., USD"}
          onChange={e => onChange("postfix", e.target.value)}
        />
      </Flex>
      <Checkbox
        id={"field-format-percentage"}
        data-testid={`${dataTestIdPrefix}-format-field-percentage`}
        onToggle={() => onChange("percentage", !fieldFormat?.percentage)}
        label={appstr("percentage")}
        checked={fieldFormat?.percentage}
      />
      <Checkbox
        id={"field-format-thousandsep"}
        data-testid={`${dataTestIdPrefix}-format-field-thousandsep`}
        onToggle={() => onChange("thousandsep", !fieldFormat?.thousandsep)}
        label={appstr("thousandssep")}
        checked={fieldFormat?.thousandsep}
      />
    </Flex>
  );
};

export const DateFormatOptions = ({ fieldFormat, onChange, dataTestIdPrefix }) => {
  return (
    <Flex direction={"column"} gap={"8px"}>
      <Checkbox
        id={"field-format-local-date-utc"}
        data-testid={`${dataTestIdPrefix}-format-field-convertlocal`}
        onToggle={() => onChange("convertlocal", !fieldFormat?.convertlocal)}
        label={appstr("Treat UTC 0 as Local Browser Time")}
        checked={fieldFormat?.convertlocal}
      />
    </Flex>
  );
};
